import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { ReimbursementMethodCard } from './ReimbursementMethodCard';
import {
  POLICY_STATUS,
  REIMBURSEMENT_METHODS,
} from '../../../constants';
import {
  useReimbursementScreen,
} from '../../myAccount/reimbursement/hooks/useReimbursementScreen';
import { VerifyYourAccountModal } from './VerifyYourAccountModal';
import { MARKETING_CHANNEL_ID } from '../../../theme/theme.constants';
import { ViewMoreButton } from '../ViewMoreButton';
import {
  UpdateReimbursementModal,
} from '../../myAccount/reimbursement/UpdateReimbursementModal';

import { useEditReimbursementEvent } from '../../../services/mixpanel';
import { useMixpanel } from '../../../hooks';
import { getLastFourDigits } from './reimbursement.utils';
import {
  useReimbursementValidations,
} from './hooks/useReimbursementValidations';
import { globalContactUsModalToggled } from '../../../actions';

import styles from './Reimbursement.module.css';

export const Reimbursement = ({ selectedPolicy = {} }) => {
  const { t } = useTranslation('insurance');
  const dispatch = useDispatch();
  const { sendEvent } = useMixpanel();
  const { EV58, SOURCES, loadingClaims } = useEditReimbursementEvent();
  const [verifyAccounModalVisible, setVerifyAccountVisible] = useState(false);
  const [
    contactInformationMissingModalVisible,
    setContactInformationMissingModalVisible,
  ] = useState(false);

  const {
    CareCredit,
    MarketChannelId,
    ReimbursementOption: {
      AccountNumberLastFour,
      CheckAddress,
      ReimbursementMethodId,
    },
    Status,
  } = selectedPolicy;

  const {
    onOpenReimbursementMethod,
  } = useReimbursementScreen({ breadCrumbs: null });

  const {
    doSendVerificationCode,
    doVerifyCode,
    errorMessage,
    getContactMethod,
    getMaskedContact,
    loading,
    missingContactInformation,
    remainingSeconds,
    shouldVerifyReimbursement,
    verified,
  } = useReimbursementValidations();

  const hasReimbursementMethod = useMemo(() => (ReimbursementMethodId
    !== REIMBURSEMENT_METHODS.none), [ReimbursementMethodId]);

  const isRedirectAvailable = useMemo(() => (
    Status === POLICY_STATUS.active
    || (Status === POLICY_STATUS.future
      && MarketChannelId === MARKETING_CHANNEL_ID.figo)
  ), [
    Status,
    MarketChannelId,
  ]);

  const onCloseVerifyModal = useCallback(() => {
    setVerifyAccountVisible(false);
  }, []);

  const doOpenReimbursementMethod = useCallback(() => {
    sendEvent(EV58({ source: SOURCES.insurance }));
    onOpenReimbursementMethod({
      policyInfo: selectedPolicy,
    });
  }, [sendEvent, onOpenReimbursementMethod]);

  useEffect(() => {
    if (verified) {
      onCloseVerifyModal();
      doOpenReimbursementMethod();
    }
  }, [verified, onCloseVerifyModal]);

  const onCloseContactInformationMissing = useCallback(() => {
    setContactInformationMissingModalVisible(false);
  }, []);

  const onContactUsClick = useCallback(() => {
    onCloseContactInformationMissing();
    dispatch(globalContactUsModalToggled());
  }, [onCloseContactInformationMissing]);

  const handleReimbursementMethod = useCallback(() => {
    if (shouldVerifyReimbursement(MarketChannelId)) {
      if (missingContactInformation(MarketChannelId)) {
        setContactInformationMissingModalVisible(true);
        return;
      }

      setVerifyAccountVisible(true);
      return;
    }

    // othwewise, we just open the reimbursement method screen regularly
    doOpenReimbursementMethod();
  }, [
    doOpenReimbursementMethod,
    selectedPolicy,
    shouldVerifyReimbursement,
  ]);

  return (
    <div className={styles.reimbursementContainer}>
      <div className={styles.reimbursementHeader}>
        <div>
          <h3 className={styles.reimbursementTitle}>{t('reimbursement')}</h3>

          <span className={styles.reimbursementText}>
            {t('reimbursementText')}
          </span>
        </div>

        {hasReimbursementMethod && isRedirectAvailable
          && (
            <>
              <ViewMoreButton
                disabled={loadingClaims}
                onClick={handleReimbursementMethod}
                text={t('reimbursementEdit')}
              />

              <VerifyYourAccountModal
                contactMethod={getContactMethod(MarketChannelId)}
                errorMessage={errorMessage}
                loading={loading}
                maskedContact={getMaskedContact(MarketChannelId)}
                onClose={onCloseVerifyModal}
                onSendCode={doSendVerificationCode(MarketChannelId)}
                onVerifyCode={doVerifyCode(MarketChannelId)}
                remainingSeconds={remainingSeconds}
                visible={verifyAccounModalVisible}
              />

              <UpdateReimbursementModal
                onButtonClick={onContactUsClick}
                onClose={onCloseContactInformationMissing}
                show={contactInformationMissingModalVisible}
              />
            </>
          )}
      </div>

      <div className={styles.reimbursementContent}>
        <h5>{t('reimbursementMethod')}</h5>

        <ReimbursementMethodCard
          addressCheck={CheckAddress}
          careCredit={CareCredit}
          isRedirectAvailable={isRedirectAvailable}
          lastNumbers={getLastFourDigits(AccountNumberLastFour)}
          marketChannelId={MarketChannelId}
          methodType={ReimbursementMethodId}
          onReimbursementMethod={handleReimbursementMethod}
        />
      </div>
    </div>
  );
};
